import React, { useEffect, useMemo, useState } from "react";
import CryptoJS from "crypto-js";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useAlert } from "../context/AlertContext";
import { useCart } from "../context/CartContext";
import Swal from "sweetalert2";
import NewTicket from "./NewTicket";
import "./MyBookings.css";
import {
  FaReceipt,
  FaFilter,
  FaThLarge,
  FaBolt,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaArrowLeft,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUser,
  FaCar,
  FaBoxOpen,
  FaCartPlus,
  FaChevronDown,
  FaDownload,
  FaInfoCircle,
  FaPhone,
  FaTicketAlt,
  FaRedo,
  FaPlay,
  FaMapPin,
  FaTools,
  FaCheck,
  FaTimes,
  FaClipboardCheck,
  FaUserCheck,
  FaExclamationTriangle,
  FaTruck,
  FaWarehouse,
  FaHome,
  FaClipboardList,
} from "react-icons/fa";

const secretKey = process.env.REACT_APP_ENCRYPT_SECRET_KEY;
const BaseURL = process.env.REACT_APP_CARBUDDY_BASE_URL;
const ImageURL = process.env.REACT_APP_CARBUDDY_IMAGE_URL;

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [visibleCount, setVisibleCount] = useState(3);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const { showAlert } = useAlert();
  const { addToCart, clearCart } = useCart();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [activeTab, setActiveTab] = useState("All"); // All | Active | Completed | Cancelled
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedBookingIds, setExpandedBookingIds] = useState(new Set());
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const bytes = CryptoJS.AES.decrypt(user.id, secretKey);
  const decryptedCustId = bytes.toString(CryptoJS.enc.Utf8);
  const token = user?.token;
  const location = useLocation();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState("");
  const [showCancelSection, setShowCancelSection] = useState(false);
  const [technicianRating, setTechnicianRating] = useState(0);
  const [cancelReasons, setCancelReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [otherChecked, setOtherChecked] = useState(false);
  const [showFeedbackSection, setShowFeedbackSection] = useState(false);
  const [serviceQuality, setServiceQuality] = useState(0);
  const [feedbackExists, setFeedbackExists] = useState(false);
  const [showResumeForm, setShowResumeForm] = useState(false);
  const [resumeDate, setResumeDate] = useState("");
  const [resumePaymentMethod, setResumePaymentMethod] = useState("");
  const [selectedResumeTimes, setSelectedResumeTimes] = useState([]);
  const [resumeMorningSlots, setResumeMorningSlots] = useState([]);
  const [resumeAfternoonSlots, setResumeAfternoonSlots] = useState([]);
  const [resumeEveningSlots, setResumeEveningSlots] = useState([]);
  const [isSubmittingResume, setIsSubmittingResume] = useState(false);
  const [showPackagesOpen, setShowPackagesOpen] = useState(false);
  const [expandedPackageIdxs, setExpandedPackageIdxs] = useState(new Set());
  const [expandedAddOnIdxs, setExpandedAddOnIdxs] = useState(new Set());
  const [couponList, setCouponList] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponApplied, setCouponApplied] = useState(false);
  const [showCouponPicker, setShowCouponPicker] = useState(false);
  const [isProcessingBookAgain, setIsProcessingBookAgain] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(""); // "processing" | "success" | "error"
  const [showRaisedTicketModal, setShowRaisedTicketModal] = useState(false);
  const [ticketDescription, setTicketDescription] = useState("");
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [isAddOnsOpen, setIsAddOnsOpen] = useState(false);
  const [showInspectionResults, setShowInspectionResults] = useState(false);
  // State to track which includes are expanded (e.g., { 0: true, 1: false })
  const [expandedIncludes, setExpandedIncludes] = React.useState({});

  const toggleIncludes = (idx) => {
    setExpandedIncludes((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const handleBack = () => {
    setSelectedBooking(null);
  };

  const getBookingStatusClass = (status) => {
    switch (status) {
      case "Completed":
      case "Success":
        return "success";
      case "Pending":
        return "pending";
      case "Confirmed":
      case "Assigned":
      case "JourneyStarted":
        return "confirmed";
      default:
        return "danger";
    }
  };

  const handleRaisedTicket = () => {
    setShowRaisedTicketModal(true);
    setTicketDescription("");
  };

  const handleSubmitTicket = async () => {
    if (!ticketDescription.trim()) {
      showAlert("Please enter a description for the ticket.", "warning");
      return;
    }

    try {
      setIsSubmittingTicket(true);
      const payload = {
        custID: parseInt(decryptedCustId),
        bookingID: selectedBooking.BookingID,
        description: ticketDescription.trim(),
      };

      const response = await axios.post(`${BaseURL}Tickets`, payload, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 || response.status === 201) {
        showAlert("Ticket raised successfully!", "success");
        setShowRaisedTicketModal(false);
        setTicketDescription("");
      } else {
        showAlert("Failed to raise ticket. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error raising ticket:", error);
      showAlert("Error while raising ticket. Please try again.", "error");
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const fetchBookings = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${BaseURL}Bookings/${decryptedCustId}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      });

      const data = res.data;
      if (Array.isArray(data) && data.length > 0) {
        setBookings(data);
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentTab = searchParams.get("tab");

    fetchBookings();

    // Add event listener for notificationReceived event
    const handleNotification = () => {
      fetchBookings();
    };
    window.addEventListener("notificationReceived", handleNotification);

    return () => {
      window.removeEventListener("notificationReceived", handleNotification);
    };
  }, []); // 👀 Watch for URL search param changes

  // If URL contains bookingId, auto-select that booking when data is loaded
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const targetId = params.get("bookingId");
      if (!targetId) return;
      if (!Array.isArray(bookings) || bookings.length === 0) return;
      const found = bookings.find(
        (b) =>
          String(b.BookingID) === String(targetId) ||
          String(b.BookingTrackID) === String(targetId),
      );
      if (found) setSelectedBooking(found);
    } catch (_) {
      /* no-op */
    }
  }, [bookings, location.search]);

  // Tab counts
  const { allCount, activeCount, completedCount, cancelledCount } =
    useMemo(() => {
      const all = bookings.length;
      const completed = bookings.filter(
        (b) => b.BookingStatus === "Completed",
      ).length;
      const cancelled = bookings.filter(
        (b) => b.BookingStatus === "Cancelled",
      ).length;
      const active = bookings.filter(
        (b) =>
          !["Completed", "Cancelled", "Failed", "Refunded"].includes(
            b.BookingStatus,
          ),
      ).length;
      return {
        allCount: all,
        activeCount: active,
        completedCount: completed,
        cancelledCount: cancelled,
      };
    }, [bookings]);

  useEffect(() => {
    // keep legacy statusFilter roughly in sync for any dependent logic
    if (activeTab === "All") setStatusFilter("All");
    else if (activeTab === "Completed") setStatusFilter("Completed");
    else if (activeTab === "Cancelled") setStatusFilter("Cancelled");
    else setStatusFilter("Active");
  }, [activeTab]);

  // Define filteredBookings with tab and search
  const filteredBookings = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return bookings.filter((booking) => {
      const matchesSearch = booking.BookingTrackID?.toString()
        .toLowerCase()
        .includes(q);
      let matchesTab = true;
      if (activeTab === "Completed")
        matchesTab = booking.BookingStatus === "Completed";
      else if (activeTab === "Cancelled")
        matchesTab = booking.BookingStatus === "Cancelled";
      else if (activeTab === "Active")
        matchesTab = !["Completed", "Cancelled", "Failed", "Refunded"].includes(
          booking.BookingStatus,
        );
      return matchesSearch && matchesTab;
    });
  }, [bookings, searchTerm, activeTab]);

  // Infinite scroll effect
  useEffect(() => {
    const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const threshold = 100; // Load more when 100px from bottom

      if (scrollHeight - scrollTop <= clientHeight + threshold) {
        if (visibleCount < filteredBookings.length && !isLoadingMore) {
          setIsLoadingMore(true);
          // Simulate loading delay for better UX
          setTimeout(() => {
            setVisibleCount((prev) => prev + 3);
            setIsLoadingMore(false);
          }, 500);
        }
      }
    };

    const scrollContainer = document.querySelector(
      ".bookings-scroll-container",
    );
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [visibleCount, filteredBookings.length, isLoadingMore]);

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!selectedBooking) return;

      try {
        const { CustID, TechID, BookingID } = selectedBooking;
        const response = await axios.get(`${BaseURL}Feedback/feedback`, {
          params: {
            custId: CustID,
            techId: TechID,
            bookingId: BookingID,
          },
          headers: {
            Authorization: `Bearer ${user?.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200 && response.data) {
          console.log("Feedback data:", response.data[0].ServiceReview);
          setFeedback(response.data[0].ServiceReview || "");
          setTechnicianRating(parseInt(response.data[0].TechRating) || 0);
          setServiceQuality(parseInt(response.data[0].ServiceRating) || 0);
          setFeedbackExists(true);
        }
      } catch (error) {
        console.error("Error fetching existing feedback:", error);
      }
    };

    fetchFeedback();
  }, [selectedBooking]);

  // Request Refund handler
  const handleRequestRefund = async () => {
    if (!selectedBooking) return;
    try {
      const payload = {
        bookingID: selectedBooking.BookingID,
        isRefunded: true,
      };
      const res = await axios.put(`${BaseURL}Payments`, payload, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.status === 200) {
        showAlert("Refund requested successfully.");
        // Optimistically update local state
        setSelectedBooking((prev) =>
          prev
            ? {
              ...prev,
              BookingStatus:
                prev.BookingStatus === "Cancelled"
                  ? prev.BookingStatus
                  : "Refunded",
              Payments: Array.isArray(prev.Payments)
                ? [
                  {
                    ...prev.Payments[0],
                    isRefunded: true,
                    IsRefunded1: true,
                    RefundStatus: null,
                  },
                  ...prev.Payments.slice(1),
                ]
                : prev.Payments,
            }
            : prev,
        );
        setBookings((prev) =>
          prev.map((b) =>
            b.BookingID === selectedBooking.BookingID
              ? {
                ...b,
                BookingStatus:
                  b.BookingStatus === "Cancelled"
                    ? b.BookingStatus
                    : "Refunded",
              }
              : b,
          ),
        );
      } else {
        showAlert("Failed to request refund. Please try again.");
      }
    } catch (err) {
      console.error("Refund request error:", err);
      showAlert("Error while requesting refund. Please try again.");
    }
  };

  // Populate payment method for resume when opening details
  useEffect(() => {
    if (selectedBooking) {
      setResumePaymentMethod(selectedBooking?.paymentMethod || "");
    }
  }, [selectedBooking]);

  // Fetch coupons for Pay Now (full view)
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const response = await axios.get(`${BaseURL}Coupons`, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });
        const now = new Date();
        const formatted = (response.data || [])
          .filter((coupon) => {
            const from = new Date(coupon.ValidFrom);
            const till = new Date(coupon.ValidTill);
            return coupon.Status && from <= now && now <= till;
          })
          .map((coupon) => ({
            id: coupon.CouponID,
            Code: coupon.Code,
            Description: coupon.Description,
            DiscountValue: coupon.DiscountValue,
            DiscountType: coupon.DiscountType,
            MaxDisAmount: coupon.MaxDisAmount,
            MinBookingAmount: coupon.MinBookingAmount,
            validTill: new Date(coupon.ValidTill),
          }));
        setCouponList(formatted);
      } catch (err) {
        console.error("Error fetching coupons (MyBookings)", err);
      }
    };

    fetchCoupons();
  }, [BaseURL, user?.token]);

  const getBookingOriginalTotal = (booking) => {
    const base = Number(booking?.TotalPrice) || 0;
    const gst = Number(booking?.GSTAmount) || 0;
    return base;
  };

  const computeCouponDiscount = (originalTotal) => {
    if (!appliedCoupon) return 0;
    let discount = 0;
    if (appliedCoupon.DiscountType === "percentage") {
      discount = (originalTotal * appliedCoupon.DiscountValue) / 100;
      if (appliedCoupon.MaxDisAmount && discount > appliedCoupon.MaxDisAmount) {
        discount = appliedCoupon.MaxDisAmount;
      }
    } else {
      discount = appliedCoupon.DiscountValue || 0;
    }
    return Math.min(discount, originalTotal);
  };

  const getBookingFinalTotalWithCoupon = (booking) => {
    const original = getBookingOriginalTotal(booking);
    const discount = computeCouponDiscount(original);
    return Math.max(original - discount, 0);
  };

  const handleApplyCouponFullView = (coupon) => {
    const original = getBookingOriginalTotal(selectedBooking || {});
    if ((coupon.MinBookingAmount || 0) > original) {
      showAlert(
        `This coupon requires a minimum booking amount of ₹${coupon.MinBookingAmount}`,
      );
      return;
    }
    setAppliedCoupon(coupon);
    setCouponApplied(true);
    setShowCouponPicker(false);
  };

  const handleRemoveCouponFullView = () => {
    setAppliedCoupon(null);
    setCouponApplied(false);
  };

  const handlePayNow = async () => {
    if (!selectedBooking) return;
    try {
      // 1) Ensure backend knows we are paying via Razorpay
      const form = new FormData();
      form.append("BookingTrackID", selectedBooking.BookingTrackID);
      form.append("PaymentMethod", "Razorpay");
      form.append("BookingFrom", "web");

      let finalTotal, gstAmount, couponAmount;

      if (appliedCoupon) {
        // Calculate discount amount
        couponAmount = Number(
          getBookingOriginalTotal(selectedBooking) -
          getBookingFinalTotalWithCoupon(selectedBooking),
        ).toFixed(2);

        // Calculate new base amount after discount
        const discountedBaseAmount =
          getBookingFinalTotalWithCoupon(selectedBooking);

        // Calculate new GST on discounted amount (18%)
        gstAmount = Number((discountedBaseAmount * 0.18).toFixed(2));

        // Final total = discounted base + new GST
        finalTotal = Number((discountedBaseAmount + gstAmount).toFixed(2));

        form.append("CouponAmount", couponAmount);
        form.append("GSTAmount", gstAmount);
        form.append("TotalAmount", discountedBaseAmount);
      } else {
        // No coupon applied, use original amounts
        couponAmount = "0";
        gstAmount = selectedBooking.GSTAmount;
        finalTotal = Number(
          (
            selectedBooking.TotalPrice +
            selectedBooking.GSTAmount -
            selectedBooking.CouponAmount
          ).toFixed(2),
        );

        form.append("CouponAmount", couponAmount);
        form.append("GSTAmount", gstAmount);
        form.append("TotalAmount", selectedBooking.TotalPrice);
      }

      form.append("BookingDate", selectedBooking.BookingDate);
      form.append("TimeSlot", selectedBooking.TimeSlot);
      form.append("Paynowtype", "Paynow");

      const res = await axios.put(`${BaseURL}Bookings/update-booking`, form, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (res.status === 200 || res.status === 201) {
        // Send the calculated final total to Razorpay
        loadRazorpay(finalTotal, res.data);
      } else {
        showAlert("Failed to resume booking. Please try again.");
      }
    } catch (err) {
      console.error("Pay Now error:", err);
      showAlert("Error initiating payment. Please try again.");
    }
  };

  // Fetch TimeSlots for resume form
  const fetchResumeTimeSlots = async (dateStr) => {
    try {
      const res = await axios.get(`${BaseURL}TimeSlot`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      const timeSlots = res.data || [];
      const sorted = timeSlots
        .filter((s) => s?.Status === true)
        .sort((a, b) => a.StartTime.localeCompare(b.StartTime));

      const categorized = { morning: [], afternoon: [], evening: [] };

      const now = new Date();
      const twoHoursLater = new Date(now.getTime() + 2 * 60 * 60 * 1000);
      const isToday =
        dateStr &&
        new Date(dateStr).toDateString() === twoHoursLater.toDateString();

      sorted.forEach(({ StartTime, EndTime }) => {
        const [sh, sm] = StartTime.split(":").map(Number);
        const [eh, em] = EndTime.split(":").map(Number);

        const startDate = new Date(dateStr);
        startDate.setHours(sh, sm, 0, 0);
        const endDate = new Date(dateStr);
        endDate.setHours(eh, em, 0, 0);
        const isExpired = isToday && startDate <= twoHoursLater;

        const fmt = (d) =>
          d.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        const label = `${fmt(startDate)} - ${fmt(endDate)}`;

        const slot = { label, disabled: isExpired };
        if (sh < 12) categorized.morning.push(slot);
        else if (sh < 16) categorized.afternoon.push(slot);
        else categorized.evening.push(slot);
      });

      setResumeMorningSlots(categorized.morning);
      setResumeAfternoonSlots(categorized.afternoon);
      setResumeEveningSlots(categorized.evening);
    } catch (err) {
      console.error("Error fetching time slots:", err);
    }
  };

  // Fetch and filter cancel reasons
  const fetchCancelReasons = async () => {
    try {
      const response = await axios.get(`${BaseURL}AfterServiceLeads`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Filter for cancel reasons only
      const cancelReasons = response.data.filter(
        (item) => item.ReasonType === "Cancel" && item.IsActive === true,
      );

      setCancelReasons(cancelReasons);
    } catch (error) {
      console.error("Error fetching cancel reasons:", error);
      return [];
    }
  };

  useEffect(() => {
    if (showResumeForm && resumeDate) {
      fetchResumeTimeSlots(resumeDate);
    }
  }, [showResumeForm, resumeDate]);

  const handleOpenResume = () => {
    setShowCancelSection(false);
    setShowResumeForm(true);
    setSelectedResumeTimes([]);
    // Default date to today if empty
    if (!resumeDate) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      setResumeDate(`${yyyy}-${mm}-${dd}`);
    }
  };

  const handleResumeSubmit = async () => {
    if (!selectedBooking) return;
    if (
      !resumeDate ||
      selectedResumeTimes.length === 0 ||
      !resumePaymentMethod
    ) {
      showAlert(
        "Please select date, at least one timeslot and payment method.",
      );
      return;
    }

    try {
      setIsSubmittingResume(true);
      const form = new FormData();
      form.append("BookingTrackID", selectedBooking.BookingTrackID);
      form.append("BookingDate", resumeDate);
      form.append("TimeSlot", selectedResumeTimes.join(","));
      form.append("PaymentMethod", resumePaymentMethod);
      form.append("BookingFrom", "web");
      form.append("CouponAmount", selectedBooking.CouponAmount);
      form.append("GSTAmount", selectedBooking.GSTAmount);
      form.append("TotalAmount", selectedBooking.TotalPrice);

      const res = await axios.put(`${BaseURL}Bookings/update-booking`, form, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (res.status === 200 || res.status === 201) {
        // alert("Booking resumed successfully!");

        if (resumePaymentMethod === "razorpay") {
          const finalTotal = (
            selectedBooking.TotalPrice +
            selectedBooking.GSTAmount -
            selectedBooking.CouponAmount
          ).toFixed(2);
          loadRazorpay(finalTotal, res.data);
        } else {
          showAlert(
            "success",
            "Booking resumed successfully!",
            3000,
            "success",
          );
          setShowResumeForm(false);
          setSelectedBooking(null);
          fetchBookings();
        }
      } else {
        showAlert("Failed to resume booking. Please try again.");
      }
    } catch (err) {
      console.error("Error resuming booking:", err);
      showAlert("Error while resuming booking. Please try again.");
    } finally {
      setIsSubmittingResume(false);
    }
  };

  const loadRazorpay = (amount, data) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: Math.round(Number(amount) * 100),
      currency: "INR",
      name: "MyCarBuddy a product by Glansa Solutions Pvt. Ltd.",
      order_id: data?.razorpay?.orderID,
      description: `Payment for ${selectedBooking?.BookingTrackID || selectedBooking?.BookingID
        }`,
      image: "/assets/img/MyCarBuddy-Logo1.png",
      handler: function (response) {
        // Wait for 5 seconds before calling confirm-payment (backend settlement time)
        setPaymentStatus("processing");
        setPaymentMessage("Please wait... your booking is being processed.");
        setShowPaymentModal(true);
        setTimeout(async () => {
          try {
            const res = await axios.post(
              `${BaseURL}Bookings/confirm-Payment`,
              {
                bookingID: data.bookingID,
                amountPaid: amount,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
                razorpayOrderId: response.razorpay_order_id,
                paymentMode: "Razorpay",
              },
              { headers: { Authorization: `Bearer ${token}` } },
            );

            if (res?.data?.success || res?.status === 200) {
              setPaymentStatus("success");
              setPaymentMessage("Payment was successful!");
              fetchBookings();
              setShowResumeForm(false);
              // setSelectedResumeTimes([]);
              clearCart();
            } else {
              setPaymentStatus("error");
              setPaymentMessage("Payment failed! Please try again.");
            }
          } catch (error) {
            console.error(error);
            setPaymentStatus("error");
            setPaymentMessage("Payment failed! Please try again.");
          }
        }, 5000);
      },
      prefill: {
        name: selectedBooking?.CustFullName,
        email: selectedBooking?.CustEmail,
        contact: selectedBooking?.CustPhoneNumber,
      },
      theme: { color: "#1890ae" },
      modal: {
        ondismiss: function () {
          setPaymentStatus("error");
          setPaymentMessage("Payment window closed.");
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // New function to handle showing cancel section
  const openCancelModal = () => {
    setShowCancelSection(true);
    setSelectedReason("");
    setOtherReason("");
    setOtherChecked(false);
    fetchCancelReasons();
  };

  // New function to handle submitting cancellation with reason
  const submitCancellation = async () => {
    // Confirm before submitting cancellation
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to cancel this booking?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, cancel it",
      cancelButtonText: "No, keep booking",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    if (!selectedReason && !otherChecked) {
      showAlert(
        "Please select a reason or choose 'Other' and provide a reason.",
      );
      return;
    }
    if (otherChecked && !otherReason.trim()) {
      showAlert("Please provide a reason in the text area.");
      return;
    }

    const reasonToSend = otherChecked ? otherReason.trim() : selectedReason;

    try {
      const payload = {
        bookingID: selectedBooking.BookingID,
        cancelledBy: decryptedCustId || "",
        reason: reasonToSend,
        refundStatus: "Pending",
        paymentStatus: selectedBooking.Payments?.[0]?.PaymentStatus || "",
      };

      const response = await axios.post(`${BaseURL}Cancellations`, payload, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        setShowCancelSection(false);

        if (
          selectedBooking.paymentMethod === "Razorpay" ||
          selectedBooking.paymentMethod === "razorpay"
        ) {
          showAlert(
            "Booking cancellation submitted successfully. Please wait for refund.",
          );
        } else {
          showAlert("Booking cancellation submitted successfully.");
        }

        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking.BookingID === selectedBooking.BookingID
              ? { ...booking, BookingStatus: "Cancelled" }
              : booking,
          ),
        );
        setSelectedBooking((prev) => ({ ...prev, BookingStatus: "Cancelled" }));
      } else {
        alert("Failed to submit cancellation. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting cancellation:", error);
      alert("Something went wrong while submitting cancellation.");
    }
  };

  const handleSubmitReview = async (bookingID) => {
    if (!selectedBooking) {
      showAlert("No booking is selected for feedback.", "warning");
      return;
    }

    if (!serviceQuality && !technicianRating && !feedback.trim()) {
      showAlert(
        "Please add a rating or feedback before submitting your review.",
        "warning",
      );
      return;
    }

    try {
      const payload = {
        bookingID: bookingID,
        custID: selectedBooking.CustID, // from selected booking
        techID: selectedBooking?.TechID ?? 0, // fallback when no technician is assigned
        techReview: "",
        serviceReview: feedback,
        techRating: String(technicianRating), // convert to string
        serviceRating: String(serviceQuality), // convert to string
      };

      const response = await axios.post(`${BaseURL}Feedback`, payload, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        setFeedbackExists(true);
        Swal.fire({
          icon: "success",
          title: "Thank You!",
          text: "Feedback submitted successfully!",
          confirmButtonColor: "#088178",
        });
      } else {
        showAlert("Failed to submit feedback. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Something went wrong while submitting feedback.";
      showAlert(backendMessage, "error");
    }
  };

  const StarRating = ({ rating, onRatingChange }) => {
    return (
      <div className="d-flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            onClick={() => onRatingChange(star)}
            style={{
              cursor: "pointer",
              color: star <= rating ? "gold" : "#ccc",
              fontSize: "1.5rem",
              marginRight: "5px",
            }}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  // Skeleton Loading Component
  const BookingSkeleton = () => {
    return (
      <div className="mb-skeleton-card">
        <div className="mb-skeleton-header"></div>
        <div className="mb-skeleton-body">
          <div className="mb-skeleton-line"></div>
          <div className="mb-skeleton-line"></div>
          <div className="mb-skeleton-line"></div>
        </div>
      </div>
    );
  };

  // const visibleBookings = Array.isArray(filteredBookings)
  //   ? filteredBookings.filter((booking) => {
  //     // If no temp addons → show booking
  //     if (!Array.isArray(booking.BookingsTempAddons)) return true;

  //     // Hide booking if ANY addon is not confirmed
  //     return booking.BookingsTempAddons.every(
  //       (addon) => addon.IsSupervisor_Confirm === 1
  //     );
  //   })
  //   : [];

  const visibleBookings = Array.isArray(filteredBookings)
    ? filteredBookings.filter((booking) => {
      const hasTempAddons =
        Array.isArray(booking.BookingsTempAddons) &&
        booking.BookingsTempAddons.length > 0;
      const hasAddOns =
        Array.isArray(booking.BookingAddOns) &&
        booking.BookingAddOns.length > 0;

      // Hide booking if BOTH are null/empty
      if (!hasTempAddons && !hasAddOns) return false;

      // If temp addons exist, hide if ANY addon is not confirmed
      if (hasTempAddons) {
        return booking.BookingsTempAddons.every(
          (addon) => addon.IsSupervisor_Confirm === 1,
        );
      }

      // Has only approved addons → show
      return true;
    })
    : [];

  let leadVehicle = null;

  if (Array.isArray(selectedBooking?.Leads)) {
    // Case 1: Leads is an array
    leadVehicle = selectedBooking.Leads.slice()
      .reverse()
      .find((lead) => lead?.Vehicle)?.Vehicle;
  } else if (selectedBooking?.Leads?.Vehicle) {
    // Case 2: Leads is a single object
    leadVehicle = selectedBooking.Leads.Vehicle;
  }

  const vehicleData = {
    number:
      selectedBooking?.VehicleNumber ||
      leadVehicle?.RegistrationNumber ||
      "N/A",

    brand: selectedBooking?.BrandName || leadVehicle?.BrandName || "N/A",

    model: selectedBooking?.ModelName || leadVehicle?.ModelName || "N/A",

    fuel: selectedBooking?.FuelTypeName || leadVehicle?.FuelTypeName || "N/A",

    image: selectedBooking?.VehicleImage
      ? `${ImageURL}${selectedBooking.VehicleImage}`
      : "/assets/img/normal/car-placeholder.jpg",
  };

  const hasVehicleInfo =
    vehicleData.number !== "N/A" ||
    vehicleData.brand !== "N/A" ||
    vehicleData.model !== "N/A" ||
    vehicleData.fuel !== "N/A";

  const totalPaidAmount = (selectedBooking?.Payments || []).reduce(
    (sum, payment) =>
      payment.PaymentStatus === "Success" ||
        payment.PaymentStatus === "Partialpaid"
        ? sum + Number(payment.AmountPaid || 0)
        : sum,
    0,
  );

  const inspectionTrackings = Array.isArray(selectedBooking?.InspectionTracking)
    ? selectedBooking.InspectionTracking.filter(Boolean)
    : [];

  const inspectionResults = Array.isArray(selectedBooking?.Inspection_Results)
    ? selectedBooking.Inspection_Results
    : [];

  useEffect(() => {
    setShowInspectionResults(false);
  }, [selectedBooking]);

  const inspectionTotalPrice = inspectionTrackings.reduce(
    (sum, inspection) => sum + Number(inspection?.TotalPrice || 0),
    0,
  );

  const renderInspectionSummaryRows = (formatPrice) => {
    if (!inspectionTotalPrice) return null;

    return inspectionTrackings.map((inspection) => (
      <div
        key={`inspection-summary-${inspection.Id || inspection.ServiceId || inspection.ServiceName}`}
        className="d-flex justify-content-between mb-2"
      >
        <div className="fw-semibold">
          {inspection.ServiceName || "Inspection"} Total
        </div>
        <div className="fw-bold text-primary">
          ₹{formatPrice(inspection.TotalPrice)}
        </div>
      </div>
    ));
  };

  return (
    <div className="mb-section">
      <div className="container py-4">
        {/* Header */}
        <div className="mb-header">
          <h2 className="mb-title">
            <span className="mb-title-icon">
              <FaReceipt />
            </span>
            My Services
          </h2>
          <button
            className="mb-filter-btn"
            onClick={() => {
              setShowFilters((s) => !s);
              setSearchTerm("");
              setActiveTab("All");
              setSelectedBooking(null);
              setShowCancelSection(false);
              setShowFeedbackSection(false);
              setShowResumeForm(false);
              setShowPackagesOpen(false);
              setExpandedPackageIdxs(new Set());
              setCouponList([]);
              setAppliedCoupon(null);
              setCouponApplied(false);
              setShowCouponPicker(false);
              setIsProcessingBookAgain(false);
              setShowPaymentModal(false);
              setPaymentMessage("");
              setPaymentStatus("");
              setShowRaisedTicketModal(false);
              setTicketDescription("");
              setIsSubmittingTicket(false);
            }}
          >
            <FaFilter /> Filter
          </button>
        </div>

        {/* Tabs */}
        {showFilters && (
          <div className="mb-tabs">
            <button
              className={`mb-tab ${activeTab === "All" ? "active" : ""}`}
              onClick={() => setActiveTab("All")}
            >
              <FaThLarge /> All
              <span className="mb-tab-count">{allCount}</span>
            </button>
            <button
              className={`mb-tab ${activeTab === "Active" ? "active" : ""}`}
              onClick={() => setActiveTab("Active")}
            >
              <FaBolt /> Active
              <span className="mb-tab-count">{activeCount}</span>
            </button>
            <button
              className={`mb-tab ${activeTab === "Completed" ? "active" : ""}`}
              onClick={() => setActiveTab("Completed")}
            >
              <FaCheckCircle /> Completed
              <span className="mb-tab-count">{completedCount}</span>
            </button>
            <button
              className={`mb-tab ${activeTab === "Cancelled" ? "active" : ""}`}
              onClick={() => setActiveTab("Cancelled")}
            >
              <FaTimesCircle /> Cancelled
              <span className="mb-tab-count">{cancelledCount}</span>
            </button>
          </div>
        )}

        {/* Search */}
        {showFilters && (
          <div className="mb-search-card">
            <div className="row g-3 align-items-end">
              <div className="col-md-9">
                <label className="form-label small fw-semibold text-muted">
                  Search by Booking Track ID
                </label>
                <input
                  type="text"
                  className="mb-search-input"
                  placeholder="e.g. 123456"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-3 text-end">
                <button
                  className="mb-search-clear"
                  onClick={() => setSearchTerm("")}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {!selectedBooking && (
          <div className="mb-scroll-container bookings-scroll-container">
            {isLoading ? (
              <>
                <BookingSkeleton />
                <BookingSkeleton />
                <BookingSkeleton />
              </>
            ) : Array.isArray(visibleBookings) && visibleBookings.length > 0 ? (
              visibleBookings.slice(0, visibleCount).map((booking) => {
                const tracking = Array.isArray(booking.TechnicianTracking)
                  ? booking.TechnicianTracking[0]
                  : {};
                const isGarage = booking?.ServiceType === "ServiceAtGarage";
                const pickupDelivery = Array.isArray(booking?.PickupDelivery)
                  ? booking.PickupDelivery[booking.PickupDelivery.length - 1]
                  : null;

                // Get status date from BookingStatusTracking (ServiceAtHome flow)
                const statusTracking = Array.isArray(
                  booking?.BookingStatusTracking,
                )
                  ? booking.BookingStatusTracking
                  : [];
                const getStatusDate = (status) =>
                  statusTracking.find((s) => s?.Status === status)
                    ?.Created_At || null;

                // ServiceAtHome: technician comes to customer's location (uses BookingStatusTracking)
                const firstPickup =
                  Array.isArray(booking?.PickupDelivery) &&
                    booking.PickupDelivery.length > 0
                    ? booking.PickupDelivery[0]
                    : null;
                const buddyAssignedDate =
                  firstPickup?.AssignDate ||
                  firstPickup?.CreatedDate ||
                  pickupDelivery?.AssignDate ||
                  booking?.TechAssignDate;
                const hasServiceInProgress = statusTracking.some(
                  (s) => s?.Status === "ServiceInProgress",
                );
                const statusTimelineHome = [
                  {
                    label: "Booking Created",
                    date: booking.BookingDate,
                    icon: <FaClipboardCheck />,
                  },
                  ...(booking?.Reschedules?.length
                    ? booking.Reschedules.map((r) => ({
                      label: "Rescheduled",
                      date: r.NewSchedule,
                      icon: <FaRedo />,
                    }))
                    : []),
                  {
                    label: "Buddy Assigned",
                    date: buddyAssignedDate,
                    icon: <FaUserCheck />,
                  },
                  {
                    label: "Buddy Started",
                    date: getStatusDate("BuddyStarted"),
                    icon: <FaPlay />,
                  },
                  {
                    label: "Buddy Reached",
                    date: getStatusDate("BuddyReached"),
                    icon: <FaMapPin />,
                  },
                  {
                    label: "Service Started",
                    date: getStatusDate("ServiceStarted"),
                    icon: <FaTools />,
                  },
                  ...(hasServiceInProgress
                    ? [
                      {
                        label: "Service In Progress",
                        date: getStatusDate("ServiceInProgress"),
                        icon: <FaWarehouse />,
                      },
                    ]
                    : []),
                  {
                    label: "Completed",
                    date: getStatusDate("Completed"),
                    icon: <FaCheck />,
                  },
                ];

                // ServiceAtGarage: CustomerToDealer → garage → DealerToCustomer (uses BookingStatusTracking)
                const hasSuccessfulPayment = Array.isArray(booking?.Payments)
                  ? booking.Payments.some((p) => p?.PaymentStatus === "Success")
                  : false;

                const statusTimelineGarage = [
                  {
                    label: "Booking Created",
                    date: booking.BookingDate,
                    icon: <FaClipboardCheck />,
                  },
                  ...(booking?.Reschedules?.length
                    ? booking.Reschedules.map((r) => ({
                      label: "Rescheduled",
                      date: r.NewSchedule,
                      icon: <FaRedo />,
                    }))
                    : []),
                  {
                    label: "Assigned",
                    date:
                      getStatusDate("Assigned") || pickupDelivery?.AssignDate,
                    icon: <FaUserCheck />,
                  },
                  {
                    label: "Buddy Started",
                    date: getStatusDate("BuddyStarted"),
                    icon: <FaPlay />,
                  },
                  {
                    label: "Buddy Reached",
                    date: getStatusDate("BuddyReached"),
                    icon: <FaMapPin />,
                  },
                  {
                    label: "Car Picked",
                    date: getStatusDate("CarPicked"),
                    icon: <FaTruck />,
                  },
                  ...(hasServiceInProgress
                    ? [
                      {
                        label: "Service In Progress",
                        date: getStatusDate("ServiceInProgress"),
                        icon: <FaWarehouse />,
                      },
                    ]
                    : []),
                  {
                    label: "Service Completed",
                    date: getStatusDate("ServiceCompleted"),
                    icon: <FaCheck />,
                  },
                  {
                    label: "Out for Delivery",
                    date: getStatusDate("OutForDelivery"),
                    icon: <FaTruck />,
                  },
                  {
                    label: "Delivered",
                    date: getStatusDate("Completed"),
                    icon: <FaHome />,
                  },
                  {
                    label: "Completed",
                    date: hasSuccessfulPayment
                      ? getStatusDate("Completed")
                      : null,
                    icon: <FaCheck />,
                  },
                ];

                const statusTimeline = isGarage
                  ? statusTimelineGarage
                  : statusTimelineHome;

                const fullTimeline = [
                  ...statusTimeline,
                  ...(booking.BookingStatus === "Cancelled"
                    ? [
                      {
                        label: "Cancelled",
                        date: new Date(),
                        icon: <FaTimes />,
                      },
                    ]
                    : []),
                  ...(booking.BookingStatus === "Failed"
                    ? [
                      {
                        label: "Failed",
                        date: new Date(),
                        icon: <FaExclamationTriangle />,
                      },
                    ]
                    : []),
                ];

                return (
                  <div key={booking.BookingID} className="mb-booking-card">
                    {/* Card Header */}
                    <div className="mb-card-header">
                      <div className="mb-booking-id">
                        <div className="mb-booking-icon">
                          <FaReceipt />
                        </div>
                        <div className="mb-booking-info">
                          <h4>
                            BID : #{booking.BookingTrackID}
                            {/* (<span>
                            {booking.BookingDate
                              ? new Date(booking.BookingDate).toLocaleDateString("en-GB")
                              : "N/A"}
                          </span>) */}
                          </h4>
                          <span>
                            Date :&nbsp;
                            {booking.BookingDate
                              ? new Date(
                                booking.BookingDate,
                              ).toLocaleDateString("en-GB")
                              : "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* {(booking.CompletedOTP || booking.BookingOTP) &&
                        booking.BookingStatus !== "Completed" &&
                        booking.BookingStatus !== "Cancelled" &&
                        booking.BookingStatus !== "Failed" && (
                          <div className="mb-card-badges">
                            <span className="mb-badge mb-badge-otp">
                              OTP: {booking.CompletedOTP || booking.BookingOTP}
                            </span>
                          </div>
                        )} */}

                      {/* Payment Status */}
                      {
                        <div
                          className={`payment-status ${booking?.Payments?.[booking.Payments.length - 1]
                            ?.PaymentStatus === "Success"
                            ? "success"
                            : "danger"
                            }`}
                        >
                          Payment:{" "}
                          {booking?.Payments?.[booking.Payments.length - 1]
                            ?.PaymentStatus || "Pending"}
                        </div>
                      }

                      {/* Show Approve Booking when temp addons exist, View when approved addons exist (can show both) */}
                      <div className="mb-card-actions">
                        {booking.BookingsTempAddons &&
                          booking.BookingsTempAddons.length > 0 && (
                            <button
                              className="mb-view-btn-approve"
                              onClick={() => {
                                navigate(`/confirm-bookings`, {
                                  state: {
                                    custId: decryptedCustId,
                                    bookingId: booking.BookingID,
                                    booking: booking,
                                  },
                                });
                              }}
                            >
                              <FaCheckCircle /> Approve Service (
                              {booking.BookingsTempAddons.length})
                            </button>
                          )}
                        {!(booking.BookingsTempAddons?.length > 0) ||
                          booking.BookingAddOns?.length > 0 ? (
                          <button
                            className="mb-view-btn"
                            onClick={() => setSelectedBooking(booking)}
                          >
                            <FaEye /> View
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {/* Services summary - differentiate pending vs approved */}
                    {/* {(booking.BookingsTempAddons?.length > 0 || booking.BookingAddOns?.length > 0) && (
                      <div className="mb-services-summary">
                        {booking.BookingsTempAddons?.length > 0 && (
                          <span className="mb-services-badge mb-services-badge-pending">
                            <FaClock /> {booking.BookingsTempAddons.length} pending approval
                          </span>
                        )}
                        {booking.BookingAddOns?.length > 0 && (
                          <span className="mb-services-badge mb-services-badge-approved">
                            <FaCheck /> {booking.BookingAddOns.length} approved
                          </span>
                        )}
                      </div>
                    )} */}

                    {/* Timeline */}
                    <div className="mb-timeline">
                      {/* Desktop Timeline */}
                      <div className="mb-timeline-desktop">
                        {fullTimeline.map((step, index, arr) => {
                          const isCompleted = !!step.date;
                          const isBad =
                            step.label === "Cancelled" ||
                            step.label === "Failed";

                          return (
                            <div
                              key={`${booking.BookingID}-h-${index}`}
                              className="mb-timeline-step"
                            >
                              <div className="mb-timeline-content">
                                <div
                                  className={`mb-timeline-icon ${isBad ? "error" : isCompleted ? "completed" : "pending"}`}
                                >
                                  {step.icon}
                                </div>
                                <div className="mb-timeline-label">
                                  {step.label}
                                </div>
                                <div className="mb-timeline-date">
                                  {step.date
                                    ? new Date(step.date).toLocaleDateString(
                                      "en-GB",
                                    )
                                    : "Pending"}
                                </div>
                              </div>
                              {index < arr.length - 1 && (
                                <div
                                  className={`mb-timeline-line ${isCompleted && !isBad ? "completed" : "pending"}`}
                                ></div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Mobile Timeline */}
                      <div className="mb-timeline-mobile">
                        {fullTimeline.map((step, index, arr) => {
                          const isCompleted = !!step.date;
                          const isBad =
                            step.label === "Cancelled" ||
                            step.label === "Failed";

                          return (
                            <div
                              key={`${booking.BookingID}-v-${index}`}
                              className="mb-timeline-mobile-step"
                            >
                              <div
                                className={`mb-timeline-mobile-icon mb-timeline-icon ${isBad ? "error" : isCompleted ? "completed" : "pending"}`}
                              >
                                {step.icon}
                              </div>
                              {index < arr.length - 1 && (
                                <div className="mb-timeline-mobile-line"></div>
                              )}
                              <div className="mb-timeline-mobile-content">
                                <div className="mb-timeline-mobile-label">
                                  {step.label}
                                </div>
                                <div className="mb-timeline-mobile-date">
                                  {step.date
                                    ? new Date(step.date).toLocaleDateString(
                                      "en-GB",
                                    )
                                    : "Pending"}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Alert */}
                    {/* {booking?.Payments &&
                      (booking.BookingStatus === "Pending" ||
                        booking.BookingStatus === "Confirmed" ||
                        booking.BookingStatus === "JourneyStarted") ? (
                      <div className="mb-card-alert warning">
                        <span className="mb-card-alert-text">
                          {booking?.Reschedules && booking.Reschedules.length >= 2
                            ? "Maximum reschedule limit reached. Contact support."
                            : "Need a different time? Reschedule your booking."}
                        </span>
                        {booking?.Reschedules && booking.Reschedules.length >= 2 ? (
                          <a href="tel:7075243939" className="mb-card-alert-btn primary" style={{ textDecoration: 'none' }}>
                            <FaPhone /> Contact Support
                          </a>
                        ) : (
                          <button
                            className="mb-card-alert-btn warning"
                            onClick={() => navigate(`/reschedule?bookingId=${booking.BookingID}`)}
                          >
                            Reschedule
                          </button>
                        )}
                      </div>
                    ) : !booking?.Payments ? (
                      <div className="mb-card-alert warning">
                        <span className="mb-card-alert-text">
                          Payment pending. Resume your booking to complete payment.
                        </span>
                        <button
                          className="mb-card-alert-btn primary"
                          onClick={() => {
                            setSelectedBooking(booking);
                            handleOpenResume();
                          }}
                        >
                          Resume Booking
                        </button>
                      </div>
                    ) : null} */}
                  </div>
                );
              })
            ) : (
              <div className="mb-no-bookings">
                <img
                  src="/assets/img/not-booked.png"
                  alt="No Bookings"
                  className="mb-no-bookings-img"
                />
                <h4>No bookings yet</h4>
                <p>
                  Looks like you haven't booked any services yet. Book your
                  first service!
                </p>
                <button
                  className="btn btn-primary mt-4"
                  onClick={() => navigate("/service")}
                >
                  Explore Services
                </button>
              </div>
            )}

            {/* Loading More */}
            {isLoadingMore && (
              <div className="mb-loading-more">
                <div className="mb-spinner"></div>
                <div className="text-muted">Loading more bookings...</div>
              </div>
            )}

            {/* End of Results */}
            {visibleCount >= visibleBookings.length &&
              visibleBookings.length > 0 && (
                <div className="text-center py-3">
                  <div className="text-muted">
                    <FaCheckCircle className="me-2" />
                    You've reached the end of your bookings
                  </div>
                </div>
              )}
          </div>
        )}

        {selectedBooking && (
          <div className="mb-detail-card mb-scroll-container">
            {/* Detail Header */}
            <div className="mb-detail-header">
              <div className="mb-detail-header-top">
                <button className="mb-detail-back-btn" onClick={handleBack}>
                  <FaArrowLeft /> Back
                </button>

                <div className="mb-detail-actions">
                  {/* {selectedBooking?.Payments ? (
                    selectedBooking.BookingStatus !== "Completed" &&
                      selectedBooking.BookingStatus !== "Cancelled" &&
                      selectedBooking.BookingStatus !== "Refunded" &&
                      selectedBooking.BookingStatus !== "Failed" &&
                      !showCancelSection ? (
                      <>
                        {(() => {
                          const fullTracking = Array.isArray(selectedBooking?.TechnicianTracking)
                            ? selectedBooking.TechnicianTracking[0]
                            : {};
                          const hasServiceStartedFull = !!fullTracking?.ServiceStartedAt;
                          const canReschedule = !hasServiceStartedFull && selectedBooking?.Reschedules && selectedBooking.Reschedules.length < 2;

                          return canReschedule ? (
                            <button
                              className="mb-detail-action-btn warning"
                              onClick={() => navigate(`/reschedule?bookingId=${selectedBooking.BookingID}`)}
                            >
                              <FaRedo /> Reschedule
                            </button>
                          ) : !hasServiceStartedFull && selectedBooking?.Reschedules && selectedBooking.Reschedules.length >= 2 ? (
                            <a
                              href="tel:7075243939"
                              className="mb-detail-action-btn warning"
                              style={{ textDecoration: 'none' }}
                            >
                              <FaPhone /> Contact Support
                            </a>
                          ) : null;
                        })()}
                        {(selectedBooking.BookingStatus === "Pending" ||
                          selectedBooking.BookingStatus === "Confirmed" ||
                          selectedBooking.BookingStatus === "JourneyStarted") && (
                            <button className="mb-detail-action-btn warning" onClick={() => openCancelModal()}>
                              <FaTimes /> Cancel
                            </button>
                          )}
                      </>
                    ) : null
                  ) : selectedBooking.BookingStatus !== "Failed" &&
                    selectedBooking.BookingStatus !== "Completed" &&
                    selectedBooking.BookingStatus !== "Cancelled" &&
                    selectedBooking.BookingStatus !== "Refunded" &&
                    !showResumeForm ? (
                    <button className="mb-detail-action-btn warning" onClick={handleOpenResume}>
                      <FaPlay /> Resume Booking
                    </button>
                  ) : null} */}
                  {selectedBooking.BookingsTempAddons?.length > 0 && (
                    <button
                      className="mb-detail-action-btn primary"
                      onClick={() =>
                        navigate(`/confirm-bookings`, {
                          state: {
                            custId: decryptedCustId,
                            bookingId: selectedBooking.BookingID,
                            booking: selectedBooking,
                          },
                        })
                      }
                    >
                      <FaCheckCircle /> Approve{" "}
                      {selectedBooking.BookingsTempAddons.length} Service
                      {selectedBooking.BookingsTempAddons.length > 1 ? "s" : ""}
                    </button>
                  )}
                  {selectedBooking.BookingStatus !== "Cancelled" && (!selectedBooking.Tickets || selectedBooking.Tickets.some(ticket => ticket.StatusName === "Closed")) && (
                    <button
                      className="mb-detail-action-btn warning"
                      onClick={() => setShowNewTicket(true)}
                    >
                      <FaTicketAlt /> Raise Ticket
                    </button>
                  )}
                </div>
              </div>

              <div className="mb-detail-info">
                <div className="mb-detail-info-item">
                  <label>Booking ID</label>
                  <h3>#{selectedBooking.BookingTrackID}</h3>
                </div>
                <div className="mb-detail-info-item">
                  <label>Date & Time</label>
                  {selectedBooking?.BookingDate && selectedBooking?.TimeSlot ? (
                    <span>
                      {new Date(selectedBooking.BookingDate).toLocaleDateString(
                        "en-GB",
                      )}{" "}
                      <div style={{ marginTop: "6px" }}>
                        <span style={{ fontWeight: "600" }}>Time Slots: </span>

                        {selectedBooking.TimeSlot &&
                          selectedBooking.TimeSlot.split(",").map((t, i) => {
                            const [start, end] = t.trim().split("-");

                            const formatTime = (time) => {
                              const [h, m, s] = time.split(":");
                              const date = new Date();
                              date.setHours(h, m, s);
                              return date.toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              });
                            };

                            return (
                              <span
                                key={i}
                                style={{
                                  display: "inline-block",
                                  padding: "4px 10px",
                                  margin: "4px",
                                  background: "#ffffff20",
                                  borderRadius: "6px",
                                }}
                              >
                                {formatTime(start)} - {formatTime(end)}
                              </span>
                            );
                          })}
                      </div>
                    </span>
                  ) : (
                    <span>N/A</span>
                  )}
                </div>
              </div>

              {/* <div className="mb-detail-location">
                <FaMapMarkerAlt />
                {selectedBooking?.CityName && selectedBooking?.StateName
                  ? `${selectedBooking.CityName}, ${selectedBooking.StateName}`
                  : "N/A"}
              </div> */}
            </div>

            {/* Detail Body */}
            <div className="mb-detail-body">
              {/* NewTicket Component */}
              {showNewTicket && (
                <NewTicket
                  onClose={() => setShowNewTicket(false)}
                  onTicketCreated={() => {
                    setShowNewTicket(false);
                  }}
                  selectedTicketBookingId={selectedBooking?.BookingID}
                />
              )}

              {/* Resume Booking Form or Details */}
              {showResumeForm ? (
                <div className="border rounded-4 p-3 mb-4">
                  <h6 className="mb-3">Resume booking</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Select date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={resumeDate}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={(e) => {
                          setResumeDate(e.target.value);
                          setSelectedResumeTimes([]);
                        }}
                      />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Payment method</label>
                      <div className="d-flex align-items-center gap-4 mt-1">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="resumePaymentMethod"
                            id="pm-razorpay"
                            value="razorpay"
                            checked={resumePaymentMethod === "razorpay"}
                            onChange={(e) =>
                              setResumePaymentMethod(e.target.value)
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="pm-razorpay"
                          >
                            razorpay
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="resumePaymentMethod"
                            id="pm-cos"
                            value="COS"
                            checked={resumePaymentMethod === "COS"}
                            onChange={(e) =>
                              setResumePaymentMethod(e.target.value)
                            }
                          />
                          <label className="form-check-label" htmlFor="pm-cos">
                            COS
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="form-label">
                      Select time slots (multi-select)
                    </label>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="fw-semibold mb-2">Morning</div>
                        {resumeMorningSlots.length === 0 && (
                          <div className="text-muted small">No slots</div>
                        )}
                        {resumeMorningSlots.map((s) => (
                          <div className="form-check" key={`m-${s.label}`}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`m-${s.label}`}
                              disabled={s.disabled}
                              checked={selectedResumeTimes.includes(s.label)}
                              onChange={(e) => {
                                setSelectedResumeTimes((prev) =>
                                  e.target.checked
                                    ? [...prev, s.label]
                                    : prev.filter((x) => x !== s.label),
                                );
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`m-${s.label}`}
                            >
                              {s.label}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="col-md-4">
                        <div className="fw-semibold mb-2">Afternoon</div>
                        {resumeAfternoonSlots.length === 0 && (
                          <div className="text-muted small">No slots</div>
                        )}
                        {resumeAfternoonSlots.map((s) => (
                          <div className="form-check" key={`a-${s.label}`}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`a-${s.label}`}
                              disabled={s.disabled}
                              checked={selectedResumeTimes.includes(s.label)}
                              onChange={(e) => {
                                setSelectedResumeTimes((prev) =>
                                  e.target.checked
                                    ? [...prev, s.label]
                                    : prev.filter((x) => x !== s.label),
                                );
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`a-${s.label}`}
                            >
                              {s.label}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="col-md-4">
                        <div className="fw-semibold mb-2">Evening</div>
                        {resumeEveningSlots.length === 0 && (
                          <div className="text-muted small">No slots</div>
                        )}
                        {resumeEveningSlots.map((s) => (
                          <div className="form-check" key={`e-${s.label}`}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`e-${s.label}`}
                              disabled={s.disabled}
                              checked={selectedResumeTimes.includes(s.label)}
                              onChange={(e) => {
                                setSelectedResumeTimes((prev) =>
                                  e.target.checked
                                    ? [...prev, s.label]
                                    : prev.filter((x) => x !== s.label),
                                );
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`e-${s.label}`}
                            >
                              {s.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                      <button
                        className="btn btn-outline-secondary px-4 py-2"
                        onClick={() => setShowResumeForm(false)}
                        disabled={isSubmittingResume}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary px-4  py-2"
                        onClick={handleResumeSubmit}
                        disabled={isSubmittingResume}
                      >
                        {isSubmittingResume ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Timeline removed as requested */}
                  {(() => {
                    const fullTracking = Array.isArray(
                      selectedBooking?.TechnicianTracking,
                    )
                      ? selectedBooking.TechnicianTracking[0]
                      : {};
                    var hasServiceStartedFull =
                      !!fullTracking?.ServiceStartedAt;
                    return null;
                  })()}

                  <div className="mb-info-cards">
                    {/* Customer Info */}
                    <div className="mb-info-card">
                      <div className="mb-info-card-header">
                        <div className="mb-info-card-icon">
                          <FaUser />
                        </div>
                        <h6>User Details</h6>
                      </div>
                      <div className="mb-info-card-body">
                        <h5>
                          {selectedBooking.IsOthers
                            ? selectedBooking.OthersFullName
                            : selectedBooking.CustomerName}
                          <span
                            style={{
                              fontWeight: 400,
                              fontSize: "0.85rem",
                              color: "#666",
                            }}
                          >
                            {" "}
                            (
                            {selectedBooking.IsOthers
                              ? selectedBooking.OthersPhoneNumber
                              : selectedBooking.PhoneNumber}
                            )
                          </span>
                        </h5>
                        <p>
                          <strong>Address:</strong>{" "}
                          {selectedBooking?.FullAddress ||
                            selectedBooking?.Pincode
                            ? `${selectedBooking?.FullAddress || ""}${selectedBooking?.FullAddress &&
                              selectedBooking?.Pincode
                              ? ", "
                              : ""
                            }${selectedBooking?.Pincode || ""}`
                            : "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Vehicle Info */}
                    {hasVehicleInfo && (
                      <div className="mb-info-card">
                        <div className="mb-info-card-header">
                          <div className="mb-info-card-icon">
                            <FaCar />
                          </div>
                          <h6>Vehicle</h6>
                        </div>

                        <div className="mb-info-card-body">
                          <div className="mb-vehicle-info">
                            <img
                              src={vehicleData.image}
                              alt="Vehicle"
                              className="mb-vehicle-image"
                            />

                            <div className="mb-vehicle-details">
                              <p>
                                <strong>Number:</strong> {vehicleData.number}
                              </p>
                              <p>
                                <strong>Brand:</strong> {vehicleData.brand}
                              </p>
                              <p>
                                <strong>Model:</strong> {vehicleData.model}
                              </p>
                              <p>
                                <strong>Fuel:</strong> {vehicleData.fuel}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Packages Section */}
                  {selectedBooking.Packages?.length > 0 && (
                    <div className="mb-packages-section">
                      <div className="mb-section-header">
                        <h5 className="mb-section-title">
                          <FaBoxOpen /> Included Packages
                        </h5>
                        {/* <button
                      className="mb-book-again-btn"
                      onClick={() => handleAddBookingToCart(selectedBooking)}
                      disabled={isProcessingBookAgain}
                    >
                      {isProcessingBookAgain ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <FaCartPlus /> Book Again
                        </>
                      )}
                    </button> */}
                      </div>

                      <div className="mb-accordion">
                        {selectedBooking.Packages.map((pkg, idx) => {
                          const isOpen = expandedPackageIdxs.has(idx);
                          return (
                            <div className="mb-accordion-item" key={idx}>
                              <div
                                className="mb-accordion-header"
                                onClick={() => {
                                  const next = new Set(expandedPackageIdxs);
                                  if (next.has(idx)) next.delete(idx);
                                  else next.add(idx);
                                  setExpandedPackageIdxs(next);
                                }}
                              >
                                <span className="mb-accordion-title">
                                  <FaBoxOpen /> {pkg.PackageName}
                                </span>
                                <FaChevronDown
                                  className={`mb-accordion-icon ${isOpen ? "open" : ""}`}
                                />
                              </div>
                              {isOpen && (
                                <div className="mb-accordion-body">
                                  {pkg.Category?.SubCategories?.[0]?.Includes
                                    ?.length ? (
                                    <ul>
                                      {pkg.Category.SubCategories[0].Includes.map(
                                        (inc) => (
                                          <li key={inc.IncludeID}>
                                            {inc.IncludeName}
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  ) : (
                                    <p className="text-muted">
                                      No includes available.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Pending Approval - BookingsTempAddons */}
                  {selectedBooking.BookingsTempAddons?.length > 0 && (
                    <div className="mb-addons-section mb-4">
                      <h5 className="fw-semibold mb-3 d-flex align-items-center">
                        {/* <span className="badge bg-warning text-dark me-2">
                          {selectedBooking.BookingsTempAddons.length}
                        </span> */}
                        Pending Approval
                      </h5>
                      <p className="text-muted small mb-3">
                        These services require your approval. Click
                        &quot;Approve Booking&quot; above to confirm.
                      </p>
                      <div className="mb-addons-grid">
                        {selectedBooking.BookingsTempAddons.map(
                          (addOn, idx) => (
                            <div
                              key={`temp-${idx}`}
                              className="mb-addon-card mb-addon-card-pending"
                            >
                              <div className="mb-addon-header">
                                <h6 className="mb-addon-title">
                                  {addOn.ServiceName}
                                </h6>
                                {/* <span className="badge bg-warning text-dark">Pending</span> */}
                              </div>
                              <div className="mb-addon-body">
                                <div className="mb-addon-row">
                                  <span className="mb-addon-label">
                                    Parts Price
                                  </span>
                                  <span className="mb-addon-value">
                                    ₹
                                    {Number(
                                      addOn.Price || addOn.BasePrice || 0,
                                    ).toLocaleString()}
                                  </span>
                                </div>
                                <div className="mb-addon-row">
                                  <span className="mb-addon-label">
                                    Service Charges
                                  </span>
                                  <span className="mb-addon-value">
                                    ₹
                                    {Number(
                                      addOn.LabourCharges || 0,
                                    ).toLocaleString()}
                                  </span>
                                </div>
                                <div className="mb-addon-row mb-addon-row-total border-top pt-2">
                                  <span className="mb-addon-label text-dark fw-bold">
                                    Total
                                  </span>
                                  <span className="mb-addon-total">
                                    ₹
                                    {(
                                      Number(
                                        addOn.Price || addOn.BasePrice || 0,
                                      ) + Number(addOn.LabourCharges || 0)
                                    ).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Services - Approved (BookingAddOns) */}
                  {selectedBooking.BookingAddOns?.length > 0 && (
                    <div className="mb-addons-section mb-4">
                      <h5 className="fw-semibold mb-3 d-flex align-items-center">
                        {/* <span className="badge bg-success me-2">
                          {selectedBooking.BookingAddOns.length}
                        </span> */}
                        Approved Services
                      </h5>

                      <div className="accordion" id="addOnsAccordion">
                        <div className="accordion-item border-0 bg-transparent">
                          <h2 className="accordion-header">
                            <button
                              className={`accordion-button mb-addons-accordion-btn ${isAddOnsOpen ? "" : "collapsed"}`}
                              type="button"
                              onClick={() => setIsAddOnsOpen(!isAddOnsOpen)}
                            >
                              <i className="bi bi-gear-wide-connected me-2"></i>
                              Service Details & Breakdown
                            </button>
                          </h2>

                          <div
                            className={`accordion-collapse collapse ${isAddOnsOpen ? "show" : ""}`}
                          >
                            <div className="accordion-body p-1 pt-3">
                              <div className="mb-addons-grid">
                                {(selectedBooking.BookingAddOns || []).map(
                                  (addOn, idx) => (
                                    <div key={idx} className="mb-addon-card">
                                      {/* Header */}
                                      <div className="mb-addon-header">
                                        <h6 className="mb-addon-title">
                                          {addOn.ServiceName}
                                        </h6>
                                      </div>

                                      {/* Pricing Body */}
                                      <div className="mb-addon-body">
                                        <div className="mb-addon-row">
                                          <span className="mb-addon-label">
                                            Parts Price
                                          </span>
                                          <span className="mb-addon-value">
                                            ₹
                                            {Number(
                                              addOn.ServicePrice || 0,
                                            ).toLocaleString()}
                                          </span>
                                        </div>
                                        <div className="mb-addon-row">
                                          <span className="mb-addon-label">
                                            Service Charges
                                          </span>
                                          <span className="mb-addon-value">
                                            ₹
                                            {Number(
                                              addOn.LabourCharges || 0,
                                            ).toLocaleString()}
                                          </span>
                                        </div>
                                        {/* <div className="mb-addon-row">
                                        <span className="mb-addon-label">GST ({addOn.GSTPercent}%)</span>
                                        <span className="mb-addon-value">₹{Number(addOn.GSTPrice || 0).toLocaleString()}</span>
                                      </div> */}
                                        <div className="mb-addon-row">
                                          <span className="mb-addon-label">
                                            SGST ({addOn.GSTPercent / 2}%)
                                          </span>
                                          <span className="mb-addon-value">
                                            ₹
                                            {Number(
                                              addOn.GSTPrice / 2 || 0,
                                            ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </span>
                                        </div>
                                        <div className="mb-addon-row">
                                          <span className="mb-addon-label">
                                            CGST ({addOn.GSTPercent / 2}%)
                                          </span>
                                          <span className="mb-addon-value">
                                            ₹
                                            {Number(
                                              addOn.GSTPrice / 2 || 0,
                                            ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </span>
                                        </div>
                                        <div className="mb-addon-row mb-addon-row-total border-top pt-2">
                                          <span className="mb-addon-label text-dark fw-bold">
                                            Total Amount
                                          </span>
                                          <span className="mb-addon-total">
                                            ₹
                                            {Number(
                                              addOn.TotalPrice || 0,
                                            ).toLocaleString()}
                                          </span>
                                        </div>

                                        {/* Includes Dropdown Section */}
                                        {Array.isArray(addOn.Includes) &&
                                          addOn.Includes.length > 0 && (
                                            <div className="mt-3">
                                              <button
                                                className={`btn-toggle-includes w-100 ${expandedIncludes[idx] ? "active" : ""}`}
                                                onClick={() =>
                                                  toggleIncludes(idx)
                                                }
                                              >
                                                <small>
                                                  Includes (
                                                  {addOn.Includes.length})
                                                </small>
                                                <i
                                                  className={`bi bi-chevron-down transition-icon ${expandedIncludes[idx] ? "rotate" : ""}`}
                                                ></i>
                                              </button>

                                              <div
                                                className={`includes-expandable-content ${expandedIncludes[idx] ? "show" : ""}`}
                                              >
                                                <ul className="mb-addon-includes-list shadow-sm">
                                                  {addOn.Includes.map(
                                                    (item) => (
                                                      <li
                                                        key={item.IncludeID}
                                                        className="mb-addon-include-item"
                                                      >
                                                        <i className="bi bi-check2-circle text-success"></i>
                                                        <span>
                                                          {item.IncludeName}
                                                        </span>
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  ),
                                )}

                                {inspectionTrackings.map((inspection) => (
                                  <div
                                    key={`inspection-card-${inspection.Id || inspection.ServiceId || inspection.ServiceName}`}
                                    className="mb-addon-card"
                                  >
                                    <div className="mb-addon-header">
                                      <h6 className="mb-addon-title">
                                        {inspection.ServiceName || "Inspection"}
                                      </h6>
                                    </div>

                                    <div className="mb-addon-body">
                                      <div className="mb-addon-row">
                                        <span className="mb-addon-label">
                                          Inspection Charges
                                        </span>
                                        <span className="mb-addon-value">
                                          ₹
                                          {Number(
                                            inspection.LabourCharges || 0,
                                          ).toLocaleString()}
                                        </span>
                                      </div>
                                      <div className="mb-addon-row">
                                        <span className="mb-addon-label">
                                          SGST ({Number(inspection.GSTPercent || 0) / 2}%)
                                        </span>
                                        <span className="mb-addon-value">
                                          ₹
                                          {Number(
                                            Number(inspection.GSTAmount || 0) / 2,
                                          ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                      </div>
                                      <div className="mb-addon-row">
                                        <span className="mb-addon-label">
                                          CGST ({Number(inspection.GSTPercent || 0) / 2}%)
                                        </span>
                                        <span className="mb-addon-value">
                                          ₹
                                          {Number(
                                            Number(inspection.GSTAmount || 0) / 2,
                                          ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                      </div>
                                      <div className="mb-addon-row mb-addon-row-total border-top pt-2">
                                        <span className="mb-addon-label text-dark fw-bold">
                                          Total Amount
                                        </span>
                                        <span className="mb-addon-total">
                                          ₹
                                          {Number(
                                            inspection.TotalPrice || 0,
                                          ).toLocaleString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {inspectionResults.length > 0 && (
                    <div className="mb-inspection-results mt-4 mb-4">
                      <div className="mb-inspection-results-header mb-3">

                        {/* Toggle Button */}
                        <button
                          className="inspection-results-toggle-modern"
                          type="button"
                          onClick={() => setShowInspectionResults(prev => !prev)}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div className="icon-circle">
                              <FaClipboardList />
                            </div>
                            <span>
                              Inspection Results
                              <span className="count-badge">
                                {inspectionResults.length}
                              </span>
                            </span>
                          </div>

                          <FaChevronDown
                            className={`chevron ${showInspectionResults ? "rotate" : ""}`}
                          />
                        </button>

                        {/* Download Button */}
                        <a
                          className="inspection-download-btn-modern"
                          href={`${BaseURL}ServiceImages/InspectionChecklistPdf/${selectedBooking?.BookingID}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaDownload />
                          <span>Download PDF</span>
                        </a>

                      </div>

                      {showInspectionResults && (
                        <div className="mt-3 table-responsive inspection-results-table-wrapper">
                          <table className="table table-bordered table-sm inspection-results-table mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Category</th>
                                <th>Includes</th>
                                <th>Checked</th>
                                <th>Remarks</th>
                                {/* <th>Technician</th> */}
                                {/* <th>Date</th> */}
                              </tr>
                            </thead>
                            <tbody>
                              {inspectionResults.map(
                                (result, index) => (
                                  <tr
                                    key={
                                      result.Id ||
                                      `${result.Checklist_id}-${index}`
                                    }
                                  >
                                    <td>{index + 1}</td>
                                    <td>{result.Category || "-"}</td>
                                    <td>{result.Includes || "-"}</td>
                                    <td>{result.Is_Checked ? "Yes" : "No"}</td>
                                    <td>{result.Remarks || "-"}</td>
                                    {/* <td>{result.TechnicianName || "-"}</td> */}
                                    {/* <td>
                                      {result.Created_at
                                        ? new Date(
                                          result.Created_at,
                                        ).toLocaleString("en-GB", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })
                                        : "-"}
                                    </td> */}
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Booking Info Card */}
                  <div className="mb-booking-info-card">
                    <div className="mb-info-card-header">
                      <div className="mb-info-card-icon">
                        <FaInfoCircle />
                      </div>
                      <h6>Booking Information</h6>
                    </div>

                    {/* Booking Status */}
                    <div className="mb-booking-info-row">
                      <span className="mb-booking-info-label">
                        Booking Status
                      </span>
                      <span
                        className={`mb-booking-info-status ${getBookingStatusClass(
                          selectedBooking?.BookingStatus,
                        )}`}
                      >
                        {selectedBooking?.BookingStatus || "Pending"}
                      </span>
                      <span className="mb-booking-info-value">
                        #{selectedBooking?.BookingTrackID || "N/A"}
                      </span>
                    </div>

                    {/* Technician */}
                    {/* <div className="mb-booking-info-row">
                      <span className="mb-booking-info-label">Technician</span>
                      <span className={`mb-booking-info-status ${selectedBooking?.TechID ? "success" : "pending"}`}>
                        {selectedBooking?.TechID ? "Assigned" : "Pending"}
                      </span>
                      <span className="mb-booking-info-value">
                        {selectedBooking?.TechID ? (
                          <>
                            {selectedBooking.TechFullName}
                            {selectedBooking?.AssignedTimeSlot && (
                              <div className="small">Slot: {selectedBooking.AssignedTimeSlot}</div>
                            )}
                          </>
                        ) : (
                          <em>Not assigned yet</em>
                        )}
                      </span>
                    </div> */}

                    {/* Payment */}
                    <div className="mb-booking-info-row">
                      <span className="mb-booking-info-label">Payment</span>
                      <span
                        className={`mb-booking-info-status ${selectedBooking?.Payments?.[
                          selectedBooking.Payments.length - 1
                        ]?.PaymentStatus === "Success"
                          ? "success"
                          : "danger"
                          }`}
                      >
                        {selectedBooking?.Payments?.[
                          selectedBooking.Payments.length - 1
                        ]?.PaymentStatus || "Pending"}
                      </span>
                      {/* <span className="mb-booking-info-value">
                        Method: {selectedBooking?.PaymentMethod || "N/A"}
                      </span> */}
                    </div>
                  </div>

                  {/* Amount Summary + COS Pay Now */}
                  <div className="border-top pt-4">
                    {(() => {
                      // 1. Calculate AddOn Total Safely
                      const addOnTotal =
                        selectedBooking.BookingAddOns?.reduce(
                          (sum, addOn) => sum + Number(addOn.TotalPrice || 0),
                          0,
                        ) || 0;

                      const isPaid =
                        selectedBooking?.Payments?.[0]?.PaymentStatus ===
                        "Success";
                      const isCOS = selectedBooking?.PaymentMethod === "COS";
                      const isCOSUnpaid =
                        isCOS &&
                        !isPaid &&
                        ["Pending", "Confirmed", "JourneyStarted"].includes(
                          selectedBooking?.BookingStatus,
                        );

                      const hasAddOns = addOnTotal > 0;
                      const isAllPaid = isPaid && !isCOSUnpaid && !hasAddOns;

                      // Helper to safely format currency
                      const formatPrice = (val) => Number(val || 0).toFixed(2);

                      // Helper to get raw number
                      const getVal = (val) => Number(val || 0);

                      // ✅ CASE 1: Online Paid + Add-ons present
                      if (isPaid && hasAddOns) {
                        return (
                          <div className="row justify-content-between py-3">
                            {/* Left: Paid Service Amount */}
                            <div className="col-md-12">
                              <div className="card border-0 shadow-sm rounded-4 p-3">
                                <h6 className="fw-semibold mb-3 text-muted">
                                  Amount Summary
                                </h6>

                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">Parts Total</div>
                                  <div className="fw-bold text-primary">
                                    ₹{formatPrice(selectedBooking.PartsPrice)}
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">
                                    Service Charges
                                  </div>
                                  <div className="fw-bold text-primary">
                                    ₹
                                    {formatPrice(selectedBooking.LabourCharges)}
                                  </div>
                                </div>

                                {/* {renderInspectionSummaryRows(formatPrice)} */}

                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">SGST </div>
                                  <div className="fw-bold text-primary">
                                    ₹
                                    {formatPrice(
                                      getVal(selectedBooking.GSTAmount) / 2,
                                    )}
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">CGST </div>
                                  <div className="fw-bold text-primary">
                                    ₹
                                    {formatPrice(
                                      getVal(selectedBooking.GSTAmount) / 2,
                                    )}
                                  </div>
                                </div>

                                {/* {renderInspectionSummaryRows(formatPrice)} */}

                                {getVal(selectedBooking.CouponAmount) > 0 && (
                                  <div className="d-flex justify-content-between mb-2">
                                    <div className="fw-semibold">Discount</div>
                                    <div className="fw-bold text-danger">
                                      -₹
                                      {formatPrice(
                                        selectedBooking.CouponAmount,
                                      )}
                                    </div>
                                  </div>
                                )}
                                <hr />
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">Paid Amount</div>
                                  <div className="fw-bold text-success">
                                    ₹{formatPrice(getVal(totalPaidAmount))}
                                  </div>
                                </div>

                                {getVal(selectedBooking.TotalPrice) - getVal(totalPaidAmount) !== 0 && (
                                  <div className="d-flex justify-content-between mb-2">
                                    <div className="fw-semibold">Balance</div>
                                    <div className="fw-bold text-danger">
                                      ₹
                                      {formatPrice(
                                        getVal(selectedBooking.TotalPrice) -
                                        getVal(totalPaidAmount),
                                      )}
                                    </div>
                                  </div>
                                )}

                                <hr />
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold fs-5">Total</div>
                                  <div className="fw-bold text-success fs-5">
                                    ₹
                                    {formatPrice(
                                      getVal(selectedBooking.TotalPrice)

                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Right: Add-on Service Amount */}
                            {/* <div className="col-md-5">
                              <div className="card border-0 shadow-sm rounded-4 p-3">
                                <h6 className="fw-semibold mb-3 text-muted">
                                  Additional Booking Amount
                                </h6>
                                <div
                                  style={{ visibility: "hidden", height: "65px" }}
                                ></div>
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">Total Amount</div>
                                  <div className="fw-bold text-primary">
                                    ₹{formatPrice(addOnTotal)}
                                  </div>
                                </div>

                                <hr />
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold fs-5">
                                    Amount to Pay
                                  </div>
                                  <div className="fw-bold text-success fs-5">
                                    ₹{formatPrice(addOnTotal)}
                                  </div>
                                </div>
                              </div>
                            </div> */}
                          </div>
                        );
                      }

                      // ✅ CASE 2: All Paid (COS + add-ons paid OR online + no add-ons)
                      if (isAllPaid || (isPaid && !hasAddOns)) {
                        return (
                          <div className="d-flex justify-content-end py-3">
                            <div className="col-md-12">
                              <div className="card border-0 shadow-sm rounded-4 p-3">
                                <h6 className="fw-semibold mb-3 text-muted">
                                  Amount Summary
                                </h6>

                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">Amount</div>
                                  <div className="fw-bold text-primary">
                                    ₹{formatPrice(selectedBooking.TotalPrice)}
                                  </div>
                                </div>

                                {/* {renderInspectionSummaryRows(formatPrice)} */}

                                {getVal(selectedBooking.CouponAmount) > 0 && (
                                  <div className="d-flex justify-content-between mb-2">
                                    <div className="fw-semibold">Discount</div>
                                    <div className="fw-bold text-danger">
                                      -₹
                                      {formatPrice(
                                        selectedBooking.CouponAmount,
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">SGST (9%)</div>
                                  <div className="fw-bold text-primary">
                                    ₹{formatPrice(getVal(selectedBooking.GSTAmount) / 2)}
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">CGST (9%)</div>
                                  <div className="fw-bold text-primary">
                                    ₹{formatPrice(getVal(selectedBooking.GSTAmount) / 2)}
                                  </div>
                                </div> */}

                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">GST</div>
                                  <div className="fw-bold text-primary">
                                    ₹
                                    {formatPrice(
                                      getVal(selectedBooking.GSTAmount),
                                    )}
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">
                                    Service Charges
                                  </div>
                                  <div className="fw-bold text-primary">
                                    ₹
                                    {formatPrice(selectedBooking.LabourCharges)}
                                  </div>
                                </div>

                                {/* {hasAddOns && (
                                  <div className="d-flex justify-content-between mb-2">
                                    <div className="fw-semibold">
                                      Additional Total
                                    </div>
                                    <div className="fw-bold text-primary">
                                      ₹{formatPrice(addOnTotal)}
                                    </div>
                                  </div>
                                )} */}

                                <hr />
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold fs-5">Total</div>
                                  <div className="fw-bold text-success fs-5">
                                    ₹
                                    {formatPrice(
                                      getVal(selectedBooking.TotalPrice) +
                                      getVal(selectedBooking.GSTAmount) -
                                      getVal(selectedBooking.CouponAmount) +
                                      // addOnTotal +
                                      getVal(selectedBooking.LabourCharges),
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // ✅ CASE 3: COS (Paid or Unpaid) + no add-ons
                      if (isCOS && !hasAddOns) {
                        return (
                          <div className="border-top pt-4">
                            <div className="col-md-12">
                              <h6 className="fw-semibold mb-3 text-muted">
                                Amount Summary
                              </h6>

                              <div className="d-flex justify-content-between mb-2">
                                <div className="fw-semibold">Amount</div>
                                <div className="fw-bold text-primary">
                                  ₹{formatPrice(selectedBooking.TotalPrice)}
                                </div>
                              </div>

                              {/* {renderInspectionSummaryRows(formatPrice)} */}

                              {getVal(selectedBooking.CouponAmount) > 0 && (
                                <div className="d-flex justify-content-between mb-2">
                                  <div className="fw-semibold">Discount</div>
                                  <div className="fw-bold text-danger">
                                    -₹
                                    {formatPrice(selectedBooking.CouponAmount)}
                                  </div>
                                </div>
                              )}

                              <div className="d-flex justify-content-between mb-2">
                                <div className="fw-semibold">SGST </div>
                                <div className="fw-bold text-primary">
                                  ₹
                                  {formatPrice(
                                    getVal(selectedBooking.GSTAmount) / 2,
                                  )}
                                </div>
                              </div>

                              <div className="d-flex justify-content-between mb-2">
                                <div className="fw-semibold">CGST </div>
                                <div className="fw-bold text-primary">
                                  ₹
                                  {formatPrice(
                                    getVal(selectedBooking.GSTAmount) / 2,
                                  )}
                                </div>
                              </div>

                              <hr />
                              <div className="d-flex justify-content-between mb-2">
                                <div className="fw-semibold fs-5">Total</div>
                                <div className="fw-bold text-success fs-5">
                                  ₹
                                  {formatPrice(
                                    getVal(selectedBooking.TotalPrice) +
                                    getVal(selectedBooking.GSTAmount) -
                                    getVal(selectedBooking.CouponAmount),
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // ✅ CASE 4: Unpaid COS (with coupon + pay now)
                      return (
                        <>
                          <div className="mb-2">
                            <h6 className="fw-semibold mb-3 text-muted">
                              Amount Summary
                            </h6>
                            <div className="d-flex justify-content-between mb-2">
                              <div className="fw-semibold">Parts Total</div>
                              <div className="fw-bold text-primary">
                                ₹{formatPrice(selectedBooking.PartsPrice)}
                              </div>
                            </div>

                            <div className="d-flex justify-content-between mb-2">
                              <div className="fw-semibold">Service Charges</div>
                              <div className="fw-bold text-primary">
                                ₹{formatPrice(selectedBooking.LabourCharges)}
                              </div>
                            </div>

                            {/* {renderInspectionSummaryRows(formatPrice)} */}

                            {getVal(selectedBooking.CouponAmount) > 0 && (
                              <div className="d-flex justify-content-between mb-2">
                                <div className="fw-semibold">Discount</div>
                                <div className="fw-bold text-danger">
                                  -₹{formatPrice(selectedBooking.CouponAmount)}
                                </div>
                              </div>
                            )}

                            <div className="d-flex justify-content-between mb-2">
                              <div className="fw-semibold">SGST</div>
                              <div className="fw-bold text-primary">
                                ₹
                                {formatPrice(
                                  getVal(selectedBooking.GSTAmount) / 2,
                                )}
                              </div>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                              <div className="fw-semibold">CGST </div>
                              <div className="fw-bold text-primary">
                                ₹
                                {formatPrice(
                                  getVal(selectedBooking.GSTAmount) / 2,
                                )}
                              </div>
                            </div>

                            {/* <div className="d-flex justify-content-between mb-2">
                              <div className="fw-semibold">GST(18%)</div>
                              <div className="fw-bold text-primary">
                                ₹{formatPrice(getVal(selectedBooking.GSTAmount))}
                              </div>
                            </div> */}

                            <hr />
                            <div className="d-flex justify-content-between mb-2">
                              <div className="fw-semibold fs-5">Total</div>
                              <div className="fw-bold text-success fs-5">
                                ₹
                                {formatPrice(
                                  getVal(selectedBooking.TotalPrice) - getVal(selectedBooking.CouponAmount)
                                  // addOnTotal
                                )}
                              </div>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between mb-2">
                              <div className="fw-semibold">Paid Amount</div>
                              <div className="fw-bold text-success">
                                ₹{formatPrice(getVal(totalPaidAmount))}
                              </div>
                            </div>

                            {getVal(selectedBooking.TotalPrice) - getVal(selectedBooking.CouponAmount) - getVal(totalPaidAmount) !== 0 && (
                              <div className="d-flex justify-content-between mb-2">
                                <div className="fw-semibold">Balance</div>
                                <div className="fw-bold text-danger">
                                  ₹
                                  {formatPrice(
                                    getVal(selectedBooking.TotalPrice) - getVal(selectedBooking.CouponAmount) - getVal(totalPaidAmount),
                                  )}
                                </div>
                              </div>
                            )}

                            {/* {hasAddOns && (
                              <div className="d-flex justify-content-between mb-2">
                                <div className="fw-semibold">Additional Total</div>
                                <div className="fw-bold text-primary">
                                  ₹{formatPrice(addOnTotal)}
                                </div>
                              </div>
                            )} */}


                          </div>
                          {/* Pay Now Button Logic Removed for brevity, add back if needed from your original code */}
                        </>
                      );
                    })()}
                  </div>

                  {/* Cancel Section Overlay */}
                  {showCancelSection && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "rgba(255,255,255,0.95)",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "start",
                        justifyContent: "center",
                        borderRadius: "1rem",
                      }}
                    >
                      <div
                        className="border rounded-3 p-4 bg-white shadow"
                        style={{ minWidth: 350, maxWidth: 500 }}
                      >
                        <h6 className="text-danger">Cancel Booking</h6>
                        <form>
                          {cancelReasons.map((reason) => (
                            <div className="form-check" key={reason.ID}>
                              <input
                                className="form-check-input"
                                type="radio"
                                name="cancelReason"
                                id={`reason-${reason.ID}`}
                                value={reason.Reason}
                                checked={
                                  selectedReason === reason.Reason &&
                                  !otherChecked
                                }
                                onChange={() => {
                                  setSelectedReason(reason.Reason);
                                  setOtherChecked(false);
                                }}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`reason-${reason.ID}`}
                              >
                                {reason.Reason}
                              </label>
                            </div>
                          ))}
                          <div className="form-check mt-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="cancelReason"
                              id="reason-other"
                              checked={otherChecked}
                              onChange={() => {
                                setOtherChecked(true);
                                setSelectedReason("");
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="reason-other"
                            >
                              Other
                            </label>
                          </div>
                          {otherChecked && (
                            <textarea
                              className="form-control mt-2"
                              rows="3"
                              placeholder="Please specify your reason"
                              value={otherReason}
                              onChange={(e) => setOtherReason(e.target.value)}
                            />
                          )}
                        </form>
                        <div className="d-flex justify-content-end mt-3">
                          <button
                            type="button"
                            className="btn btn-secondary me-2 px-4 py-2"
                            onClick={() => setShowCancelSection(false)}
                          >
                            Close
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger px-4 py-2"
                            onClick={submitCancellation}
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Review Section (only if not cancelling) */}
                  {!showCancelSection &&
                    selectedBooking.BookingStatus === "Completed" && (
                      <div className="review-section">
                        <h6 className="review-title">Rate Your Experience</h6>

                        {/* Service Quality */}
                        <div className="review-block">
                          <label className="review-label">
                            Service Quality
                          </label>
                          <p className="review-helper">
                            How satisfied were you with the overall service?
                          </p>
                          <StarRating
                            rating={serviceQuality}
                            onRatingChange={setServiceQuality}
                          />
                        </div>

                        {/* Technician Rating */}
                        {/* <div className="review-block">
                          <label className="review-label">Technician</label>
                          <p className="review-helper">
                            How would you rate the professionalism and
                            expertise?
                          </p>
                          <StarRating
                            rating={technicianRating}
                            onRatingChange={setTechnicianRating}
                          />
                        </div> */}

                        {/* Feedback */}
                        <div className="review-block">
                          <label className="review-label">Your Feedback</label>
                          <textarea
                            className="review-textarea"
                            rows="3"
                            placeholder="Share your thoughts to help us improve"
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                          />
                        </div>

                        {!feedbackExists && (
                          <button
                            className="mb-detail-back-btn-new review-submit-btn"
                            onClick={() =>
                              handleSubmitReview(selectedBooking.BookingID)
                            }
                          >
                            Submit Review
                          </button>
                        )}
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        )}

        {showPaymentModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "relative",
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "20px",
                width: "90%",
                maxWidth: "350px",
                textAlign: "center",
                boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
              }}
            >
              {paymentStatus === "processing" ? (
                <>
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      margin: "0 auto 20px",
                      border: "4px solid #1890ae",
                      borderTop: "4px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  <h4 style={{ marginBottom: 10 }}>Processing Payment</h4>
                  <p style={{ color: "#666", marginBottom: 20 }}>
                    Please wait... your booking is being processed.
                  </p>
                </>
              ) : (
                <>
                  <img
                    src={
                      paymentStatus === "success"
                        ? "https://cdn-icons-png.flaticon.com/512/190/190411.png"
                        : "https://cdn-icons-png.flaticon.com/512/463/463612.png"
                    }
                    alt="Status Icon"
                    style={{ width: 50, height: 50, marginBottom: 20 }}
                  />
                  <h4 style={{ marginBottom: 10 }}>
                    {paymentStatus === "success" ? "Successful" : " Failed"}
                  </h4>
                  <p style={{ color: "#666", marginBottom: 20 }}>
                    {paymentMessage}
                  </p>
                  <button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setAppliedCoupon(null);
                      setCouponApplied(false);
                      handleBack();
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    style={{
                      backgroundColor:
                        paymentStatus === "success" ? "#28a745" : "#dc3545",
                      color: "#fff",
                      padding: "8px 20px",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    OK
                  </button>
                </>
              )}

              {/* Spinner animation */}
              <style>
                {`
             @keyframes spin {
               0% { transform: rotate(0deg); }
               100% { transform: rotate(360deg); }
             }
           `}
              </style>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;
